import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, FileText, BookOpen, MessageCircleQuestion } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { TrackCard } from "@/components/academy/TrackCard";
import { AcademySearch } from "@/components/academy/AcademySearch";
import { AskQuestionModal } from "@/components/academy/AskQuestionModal";
import { listTracks, getFeaturedLesson } from "@/lib/academy.functions";
import { getFeaturedLeadMagnet } from "@/lib/leadMagnet.functions";
import { LeadMagnetForm } from "@/components/library/LeadMagnetForm";
import { seo } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/academy/")({
  head: () =>
    seo({
      title: "The Academy · Currency Thoughts",
      description:
        "Free financial literacy for families building generational wealth. Protection, retirement, legacy, family, and business tracks.",
      canonical: "/academy",
    }),
  loader: async ({ context }) => {
    const qc = context.queryClient;
    await Promise.all([
      qc.ensureQueryData({
        queryKey: ["academy", "tracks"],
        queryFn: () => listTracks(),
      }),
      qc.ensureQueryData({
        queryKey: ["academy", "featured"],
        queryFn: () => getFeaturedLesson(),
      }),
      qc.ensureQueryData({
        queryKey: ["family-library", "lead-magnet"],
        queryFn: () => getFeaturedLeadMagnet(),
      }),
    ]);
  },
  component: AcademyPage,
});

function AcademyPage() {
  const [askOpen, setAskOpen] = useState(false);
  const { data: tracks } = useSuspenseQuery({
    queryKey: ["academy", "tracks"],
    queryFn: () => listTracks(),
  });
  const { data: featured } = useSuspenseQuery({
    queryKey: ["academy", "featured"],
    queryFn: () => getFeaturedLesson(),
  });
  const { data: leadMagnet } = useSuspenseQuery({
    queryKey: ["family-library", "lead-magnet"],
    queryFn: () => getFeaturedLeadMagnet(),
  });

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* SECTION 1 — Hero */}
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-5xl px-5 py-20 md:px-10 md:py-28 text-center">
            <div className="flex justify-center">
              <Eyebrow color="cream">The Academy</Eyebrow>
            </div>
            <h1 className="mt-6 font-display text-[40px] leading-[1.1] text-cream-50 md:text-[56px]">
              Financial literacy is the{" "}
              <em className="italic text-bronze-400">first inheritance.</em>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl font-sans text-[17px] leading-relaxed text-cream-50/80">
              Free, structured learning paths to help families make confident money
              decisions — at every life stage.
            </p>
            <div className="flex justify-center">
              <AcademySearch />
            </div>
          </div>
        </section>

        {/* SECTION 2 — Choose Your Path */}
        <section className="bg-cream-50 py-20">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <div className="text-center">
              <div className="flex justify-center">
                <Eyebrow>Learning Tracks</Eyebrow>
              </div>
              <h2 className="mt-5 font-display text-[36px] leading-tight text-navy-900 md:text-[44px]">
                Start where you are.{" "}
                <em className="italic text-bronze-600">Build where you're going.</em>
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {tracks.map((t) => (
                <TrackCard key={t.id} track={t} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 — Lesson of the Week */}
        {featured && (
          <section className="bg-white py-16">
            <div className="mx-auto max-w-5xl px-5 md:px-10">
              <div className="text-center">
                <Eyebrow>Lesson of the Week</Eyebrow>
              </div>
              <div
                className="mt-8 ring-2 ring-bronze-600/60 bg-cream-50/40 p-8 md:p-12 shadow-[0_24px_50px_-30px_rgba(168,139,92,0.4)]"
                style={{ borderRadius: 6 }}
              >
                <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                  {featured.track?.title ?? "Featured"} ·{" "}
                  {featured.lesson.estimated_minutes} min read
                </p>
                <h3 className="mt-3 font-display text-[28px] leading-snug text-navy-900 md:text-[36px]">
                  {featured.lesson.title}
                </h3>
                {featured.lesson.excerpt && (
                  <p className="mt-4 max-w-2xl font-sans text-[16px] leading-relaxed text-charcoal-900">
                    {featured.lesson.excerpt}
                  </p>
                )}
                {featured.track && (
                  <PrimaryButton asChild size="lg" className="mt-8">
                    <Link
                      to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                      params={{
                        trackSlug: featured.track.slug,
                        lessonSlug: featured.lesson.slug,
                      }}
                    >
                      Start lesson <ArrowRight className="h-4 w-4" />
                    </Link>
                  </PrimaryButton>
                )}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 4 — From the Library */}
        <section className="bg-cream-50 py-16">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <div className="text-center">
              <Eyebrow>Reference</Eyebrow>
              <h2 className="mt-5 font-display text-[32px] text-navy-900 md:text-[40px]">
                Three places to come back to.
              </h2>
              <p className="mx-auto mt-3 max-w-xl font-sans text-[15px] text-charcoal-500">
                The Glossary, the Family Library, and Ask Currency Thoughts.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                {
                  icon: BookOpen,
                  title: "The Glossary",
                  desc: "Plain-English definitions for the language of financial planning.",
                  to: "/academy/glossary",
                },
                {
                  icon: FileText,
                  title: "The Family Library",
                  desc: "Workbooks, checklists, and templates for your next family meeting.",
                  to: "/academy/library",
                },
                {
                  icon: MessageCircleQuestion,
                  title: "Ask Currency Thoughts",
                  desc: "We answer one family money question every week.",
                  to: "/academy/ask",
                },
              ].map((w) => {
                const Icon = w.icon;
                return (
                  <Link
                    key={w.title}
                    to={w.to}
                    className="group flex h-full flex-col border border-bronze-600/20 bg-white p-6 transition-colors hover:border-bronze-600/60"
                    style={{ borderRadius: 4 }}
                  >
                    <Icon className="h-7 w-7 text-bronze-600" strokeWidth={1.4} />
                    <h3 className="mt-4 font-display text-xl text-navy-900">{w.title}</h3>
                    <p className="mt-2 font-sans text-[14px] text-charcoal-500">{w.desc}</p>
                    <span className="mt-auto inline-block pt-5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-bronze-600 transition-transform group-hover:translate-x-1">
                      Explore →
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 5 — Ask Currency Thoughts */}
        {leadMagnet && (
          <section className="bg-white py-16">
            <div className="mx-auto max-w-6xl px-5 md:px-10">
              <LeadMagnetForm resource={leadMagnet} />
            </div>
          </section>
        )}

        <section className="bg-navy-800 py-16 text-cream-50">
          <div className="mx-auto max-w-4xl px-5 md:px-10 text-center">
            <div className="flex justify-center">
              <Eyebrow color="cream">Ask Currency Thoughts</Eyebrow>
            </div>
            <h2 className="mt-5 font-display text-[32px] leading-tight md:text-[42px]">
              Have a family money question?
            </h2>
            <p className="mx-auto mt-4 max-w-xl font-sans text-[16px] text-cream-50/80">
              We answer one a week — submit yours and we'll get to it on air.
            </p>
            <PrimaryButton size="lg" className="mt-8" onClick={() => setAskOpen(true)}>
              Submit a Question
            </PrimaryButton>
          </div>
        </section>

        {/* SECTION 6 — Newsletter */}
        <section className="bg-cream-50 py-12">
          <NewsletterStrip />
        </section>
      </main>
      <Footer />
      <AskQuestionModal open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}

function NewsletterStrip() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.from("contact_messages").insert({
        full_name: "Newsletter",
        email,
        subject: "newsletter",
        body: "Newsletter signup from /academy",
      });
      if (error) throw error;
      toast.success("Subscribed. Watch your inbox.");
      trackEvent("newsletter_subscribed", { source: "academy" });
      setEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not subscribe.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 px-5 text-center md:px-10">
      <Eyebrow>The Weekly</Eyebrow>
      <h3 className="font-display text-[26px] text-navy-900 md:text-[32px]">
        Get one Family Lesson a week.
      </h3>
      <form
        onSubmit={onSubmit}
        className="mt-2 flex w-full max-w-md flex-col gap-3 sm:flex-row"
      >
        <input
          type="email"
          required
          maxLength={255}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@family.com"
          className="flex-1 border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] outline-none focus:border-bronze-600"
        />
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? "..." : "Subscribe"}
        </PrimaryButton>
      </form>
    </div>
  );
}
